import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Check } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";

const SIZES = [
  { id: "under-100", label: "Under 100 acres" },
  { id: "100-500", label: "100 – 500 acres" },
  { id: "500-2000", label: "500 – 2,000 acres" },
  { id: "2000-plus", label: "2,000+ acres" },
];

const TERRAIN = [
  { id: "pasture", label: "Open pasture" },
  { id: "cropping", label: "Cropping country" },
  { id: "bush", label: "Bush / forest edges" },
  { id: "mixed", label: "Mixed & hilly" },
];

const SPECIES = [
  { id: "pigs", label: "Feral Pigs" },
  { id: "sambar", label: "Sambar Deer" },
  { id: "red-deer", label: "Red Deer" },
  { id: "fallow", label: "Fallow Deer" },
  { id: "hog", label: "Hog Deer" },
  { id: "foxes", label: "Foxes" },
  { id: "rabbits", label: "Rabbits" },
  { id: "hares", label: "Hares" },
  { id: "dogs", label: "Wild Dogs" },
  { id: "goats", label: "Goats" },
  { id: "cats", label: "Feral Cats" },
  { id: "kangaroos", label: "Kangaroos" },
  { id: "other", label: "Other — nominate below" },
];

const PRESSURE = [
  { id: "low", label: "Low", desc: "Occasional sightings, minimal damage" },
  { id: "moderate", label: "Moderate", desc: "Regular sign and visible damage" },
  { id: "severe", label: "Severe", desc: "Stock or crop losses mounting" },
];

function buildRecommendation(a) {
  const methods = a.species.includes("other")
    ? ["On-site property assessment & species audit"]
    : ["Thermal & night-vision ground shooting program"];
  if (a.species.includes("foxes")) methods.push("Baiting & den fumigation under ACUP permit");
  if (a.species.includes("rabbits")) methods.push("Warren ripping, fumigation & harbour removal");

  const nights =
    a.size === "2000-plus" ? "4 – 6 nights" : a.size === "500-2000" ? "3 – 4 nights" : a.size === "100-500" ? "2 – 3 nights" : "1 – 2 nights";

  const followUp =
    a.pressure === "severe"
      ? "Monthly maintenance for 6 months"
      : a.pressure === "moderate"
        ? "Quarterly maintenance visits"
        : "Bi-annual pressure check-ins";

  return { methods, nights, followUp };
}

const STEPS = ["Property size", "Terrain type", "Target species", "Pest pressure"];

function OptionButton({ selected, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center justify-between border p-5 text-left transition-all duration-300 active:scale-[0.98] ${
        selected ? "border-thermal bg-thermal/10 text-bone" : "border-bone/15 text-euca hover:border-thermal/40 hover:text-bone"
      }`}
    >
      <span className="font-display text-sm font-bold uppercase tracking-wide sm:text-base">{children}</span>
      {selected && <Check className="h-4 w-4 shrink-0 text-thermal" />}
    </button>
  );
}

export default function Calculator() {
  const [step, setStep] = useState(0);
  const [size, setSize] = useState(null);
  const [terrain, setTerrain] = useState(null);
  const [species, setSpecies] = useState([]);
  const [otherSpecies, setOtherSpecies] = useState("");
  const [pressure, setPressure] = useState(null);

  const done = step === 4;
  const canNext =
    (step === 0 && size) || (step === 1 && terrain) || (step === 2 && species.length > 0) || (step === 3 && pressure);

  const toggleSpecies = (id) =>
    setSpecies((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const reset = () => {
    setStep(0);
    setSize(null);
    setTerrain(null);
    setSpecies([]);
    setPressure(null);
  };

  const rec = done ? buildRecommendation({ size, species, pressure }) : null;
  const sizeLabel = SIZES.find((s) => s.id === size)?.label;
  const terrainLabel = TERRAIN.find((t) => t.id === terrain)?.label;
  const speciesLabels = SPECIES.filter((s) => species.includes(s.id)).map((s) =>
    s.id === "other" ? otherSpecies.trim() || "Other species" : s.label
  );

  return (
    <main data-testid="calculator-page" className="pt-16">
      <section className="mx-auto max-w-5xl px-4 pb-24 pt-16 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Property Assessment Tool</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            Scope your
            <br />
            <span className="text-thermal">feral pressure.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-euca sm:text-lg">
            Four questions, sixty seconds — a first-pass indication of the program your
            property likely needs. A formal on-site audit confirms it.
          </p>
        </FadeIn>

        <FadeIn delay={0.15} className="mt-14 border border-thermal/15 bg-field p-6 sm:p-10">
          {!done && (
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal" data-testid="calculator-progress">
              Step 0{step + 1} / 04 — {STEPS[step]}
            </p>
          )}

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }} className="mt-8">
                <h2 className="font-display text-xl font-bold tracking-tight text-bone sm:text-2xl">How much country are we working?</h2>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {SIZES.map((s) => (
                    <OptionButton key={s.id} selected={size === s.id} onClick={() => setSize(s.id)} testId={`calculator-option-${s.id}`}>
                      {s.label}
                    </OptionButton>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }} className="mt-8">
                <h2 className="font-display text-xl font-bold tracking-tight text-bone sm:text-2xl">What's the dominant terrain?</h2>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {TERRAIN.map((t) => (
                    <OptionButton key={t.id} selected={terrain === t.id} onClick={() => setTerrain(t.id)} testId={`calculator-option-${t.id}`}>
                      {t.label}
                    </OptionButton>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }} className="mt-8">
                <h2 className="font-display text-xl font-bold tracking-tight text-bone sm:text-2xl">Which species are causing trouble?</h2>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-euca">Select all that apply</p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {SPECIES.map((s) => (
                    <OptionButton key={s.id} selected={species.includes(s.id)} onClick={() => toggleSpecies(s.id)} testId={`calculator-option-${s.id}`}>
                      {s.label}
                    </OptionButton>
                  ))}
                </div>
                {species.includes("other") && (
                  <div className="mt-4">
                    <label htmlFor="other-species" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-euca">
                      Nominate the species
                    </label>
                    <input
                      id="other-species"
                      data-testid="calculator-other-species-input"
                      value={otherSpecies}
                      onChange={(e) => setOtherSpecies(e.target.value)}
                      placeholder="e.g. Indian myna, starling, sparrow"
                      className="w-full border border-bone/15 bg-obsidian px-4 py-3 text-sm text-bone outline-none transition-colors duration-300 placeholder:text-euca/50 focus:border-thermal"
                    />
                  </div>
                )}
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35 }} className="mt-8">
                <h2 className="font-display text-xl font-bold tracking-tight text-bone sm:text-2xl">How heavy is the pressure?</h2>
                <div className="mt-8 grid gap-3">
                  {PRESSURE.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPressure(p.id)}
                      data-testid={`calculator-option-${p.id}`}
                      className={`flex items-center justify-between border p-5 text-left transition-all duration-300 active:scale-[0.98] ${
                        pressure === p.id ? "border-thermal bg-thermal/10" : "border-bone/15 hover:border-thermal/40"
                      }`}
                    >
                      <span>
                        <span className={`block font-display text-sm font-bold uppercase tracking-wide sm:text-base ${pressure === p.id ? "text-bone" : "text-euca"}`}>
                          {p.label}
                        </span>
                        <span className="mt-1 block text-sm text-euca">{p.desc}</span>
                      </span>
                      {pressure === p.id && <Check className="h-4 w-4 shrink-0 text-thermal" />}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {done && (
              <motion.div key="result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="calculator-result">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">Indicative Scope — First Pass</p>
                <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-bone sm:text-3xl">Your recommended program</h2>

                <div className="mt-8 grid gap-px border border-thermal/15 bg-thermal/15 sm:grid-cols-3">
                  <div className="bg-obsidian p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-euca">Property</p>
                    <p className="mt-2 font-display text-base font-bold text-bone">{sizeLabel}</p>
                    <p className="mt-1 text-sm text-euca">{terrainLabel}</p>
                  </div>
                  <div className="bg-obsidian p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-euca">Targets</p>
                    <p className="mt-2 font-display text-base font-bold text-bone">{speciesLabels.join(" · ")}</p>
                  </div>
                  <div className="bg-obsidian p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-euca">Initial Knockdown Duration</p>
                    <p className="mt-2 font-display text-base font-bold text-thermal">{rec.nights}</p>
                  </div>
                </div>

                <div className="mt-6 border border-thermal/15 bg-obsidian p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-euca">Recommended methods</p>
                  <ul className="mt-4 space-y-3">
                    {rec.methods.map((m) => (
                      <li key={m} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-thermal" />
                        <span className="text-sm text-bone/90">{m}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 border-t border-thermal/10 pt-4 text-sm text-euca">
                    Follow-up: <span className="text-bone">{rec.followUp}</span>
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    data-testid="calculator-request-audit-button"
                    className="group flex items-center gap-2 border border-thermal bg-thermal px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal active:scale-95"
                  >
                    Request On-Site Audit
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <button
                    onClick={reset}
                    data-testid="calculator-reset-button"
                    className="flex items-center gap-2 border border-bone/30 px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:border-thermal hover:text-thermal active:scale-95"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Start Over
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {!done && (
            <div className="mt-10 flex items-center justify-between border-t border-thermal/10 pt-6">
              <button
                onClick={() => setStep(step - 1)}
                disabled={step === 0}
                data-testid="calculator-back-step"
                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-euca transition-colors enabled:hover:text-thermal disabled:opacity-30"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              <button
                onClick={() => canNext && setStep(step + 1)}
                disabled={!canNext}
                data-testid="calculator-next-step"
                className="flex items-center gap-2 border border-thermal bg-thermal px-6 py-3 font-mono text-xs font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 enabled:hover:bg-transparent enabled:hover:text-thermal enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
              >
                {step === 3 ? "Generate Scope" : "Next"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </FadeIn>
      </section>
    </main>
  );
}
